/**
 * App-tour state.
 *
 * Desktop/web only — `TourProvider` is mounted by DesktopLayout, which never
 * renders on native. It is safe to call `useTour()` from a shared component
 * regardless: outside a provider the hook returns an inert tour whose
 * `available` is false, so callers render nothing rather than crashing.
 *
 * The provider owns three things:
 *   1. Which steps this user gets (computed once, at start — see `steps.ts`).
 *   2. Where they are in that list.
 *   3. Whether they have ever taken the tour, so the launcher can carry a
 *      first-run dot. Nothing here ever starts the tour on its own.
 *
 * `available` is the single gate every launcher checks. It is false outside a
 * provider (native), and false in Partner Access — a partner impersonating a
 * customer must not be offered a tour of an account that isn't theirs.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import { TOUR_STEPS } from './steps';
import { deriveAudience } from './audience';
import { useRoutes } from '../navigation/useRoutes';
import type { TourAudience, TourStep } from './types';
import type { RootState } from '../store';

/** Set once the user opens the tour — drives the launcher's first-run dot. */
export const TOUR_SEEN_KEY = '@eleplan/app_tour_seen';


interface TourValue {
  /** False when there is no provider (native) or the tour must not run here
   *  (Partner Access) — launchers hide on this one flag. */
  available: boolean;
  active: boolean;
  /** Steps this user gets — already filtered by `appliesTo`. */
  steps: TourStep[];
  index: number;
  step: TourStep | null;
  /** True until the user has opened the tour at least once. */
  isFirstRun: boolean;
  start: () => void;
  next: () => void;
  prev: () => void;
  end: () => void;
  /** Drop the current step and advance — used when an anchor never appears. */
  skipCurrent: () => void;
}

const INERT: TourValue = {
  available: false,
  active: false,
  steps: [],
  index: 0,
  step: null,
  isFirstRun: false,
  start: () => {},
  next: () => {},
  prev: () => {},
  end: () => {},
  skipCurrent: () => {},
};

const TourContext = createContext<TourValue>(INERT);

export function useTour(): TourValue {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);
  const [isFirstRun, setIsFirstRun] = useState(false);

  const projectsList = useSelector((s: RootState) => s.projects?.list);
  const account = useSelector((s: RootState) => (s as any)?.session?.account);
  const viaPartnerAccess = useSelector(
    (s: RootState) => !!((s as any).accounts?.current?.via_partner_access),
  );
  // The REAL account-chat gate — plan count AND permission — from the same
  // hook the sidebar and tab navigator use. A hand-rolled `length >= 3`
  // mirror here would drift the day the gate changes.
  const { tabs } = useRoutes();
  const hasAccountChat = tabs.some((t) => t.name === 'AI');

  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(TOUR_SEEN_KEY)
      .then((v) => { if (!cancelled) setIsFirstRun(v !== 'true'); })
      .catch(() => {/* a missing flag just means no dot */});
    return () => { cancelled = true; };
  }, []);

  const audience: TourAudience = useMemo(
    () => deriveAudience({ projectsList, account, viaPartnerAccess, hasAccountChat }),
    [projectsList, account, viaPartnerAccess, hasAccountChat],
  );

  // Ending Partner Access mid-tour flips the audience under a running tour
  // whose steps were computed for the customer's view — end it rather than
  // let it walk anchors that no longer describe what's on screen.
  useEffect(() => {
    if (viaPartnerAccess) { setActive(false); setIndex(0); }
  }, [viaPartnerAccess]);

  const start = useCallback(() => {
    if (audience.inPartnerAccess) return;
    const applicable = TOUR_STEPS.filter((s) => !s.appliesTo || s.appliesTo(audience));
    if (!applicable.length) return;
    setSteps(applicable);
    setIndex(0);
    setActive(true);
    setIsFirstRun(false);
    AsyncStorage.setItem(TOUR_SEEN_KEY, 'true').catch(() => {});
    applicable[0].navigate?.(audience);
  }, [audience]);

  const end = useCallback(() => {
    setActive(false);
    setIndex(0);
  }, []);

  const goTo = useCallback((nextIndex: number) => {
    setIndex((current) => {
      const target = Math.max(0, nextIndex);
      if (target >= steps.length) {
        // Walking off the end is how the tour finishes.
        setActive(false);
        return 0;
      }
      if (target !== current) steps[target]?.navigate?.(audience);
      return target;
    });
  }, [steps, audience]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /**
   * Remove the current step and show the next one. The overlay calls this when
   * an anchor never materialises — a step pointing at something that isn't
   * there should vanish from the tour, including from the "n of m" counter,
   * rather than sit on a blank spotlight.
   */
  const skipCurrent = useCallback(() => {
    setSteps((current) => {
      const remaining = current.filter((_, i) => i !== index);
      if (!remaining.length) setActive(false);
      else {
        const target = Math.min(index, remaining.length - 1);
        setIndex(target);
        remaining[target]?.navigate?.(audience);
      }
      return remaining;
    });
  }, [index, audience]);

  const value = useMemo<TourValue>(() => ({
    available: !audience.inPartnerAccess,
    active,
    steps,
    index,
    step: active ? steps[index] ?? null : null,
    isFirstRun,
    start,
    next,
    prev,
    end,
    skipCurrent,
  }), [audience.inPartnerAccess, active, steps, index, isFirstRun, start, next, prev, end, skipCurrent]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
